import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import useSession from "../hooks/useSession";
import type { Order, SessionState, TipMode } from "../types";
import { useApiKeyContext } from "../contexts/ApiKeyContext";
import "./SessionPage.css";

const currencyOptions = [
  "USD",
  "EUR",
  "GBP",
  "CAD",
  "AUD",
  "CHF",
  "JPY",
  "KRW",
];

const integerCurrencies = new Set(["JPY", "KRW"]);

interface ShareBreakdown {
  items: number;
  tip: number;
  total: number;
}

interface Totals {
  subtotal: number;
  tip: number;
  total: number;
  shares: Record<string, ShareBreakdown>;
}

const computeOrderTotal = (order: Order): number => {
  const baseTotal = order.perPerson
    ? order.amount * order.persons.length
    : order.amount;
  return Number.isFinite(baseTotal) ? baseTotal : 0;
};

const computeTotals = (session: SessionState | null): Totals => {
  if (!session) {
    return {
      subtotal: 0,
      tip: 0,
      total: 0,
      shares: {},
    };
  }

  const subtotal = session.orders.reduce(
    (sum, order) => sum + computeOrderTotal(order),
    0
  );
  const tip =
    session.tipMode === "percentage"
      ? subtotal * (session.tipPercentage / 100)
      : Math.max(session.totalPaid - subtotal, 0);
  const total = subtotal + tip;

  const shares = session.diners.reduce<Record<string, ShareBreakdown>>(
    (acc, diner) => {
      acc[diner] = { items: 0, tip: 0, total: 0 };
      return acc;
    },
    {}
  );

  session.orders.forEach((order) => {
    if (order.persons.length === 0) {
      return;
    }
    const orderTotal = computeOrderTotal(order);
    const portion = order.perPerson
      ? order.amount
      : orderTotal / order.persons.length;
    order.persons.forEach((person) => {
      if (!shares[person]) {
        shares[person] = { items: 0, tip: 0, total: 0 };
      }
      shares[person].items += portion;
    });
  });

  const itemsTotal = Object.values(shares).reduce(
    (sum, share) => sum + share.items,
    0
  );

  Object.keys(shares).forEach((person) => {
    const share = shares[person];
    const weight = itemsTotal > 0 ? share.items / itemsTotal : 0;
    share.tip = tip * weight;
    share.total = share.items + share.tip;
  });

  return { subtotal, tip, total, shares };
};

const formatCurrency = (currency: string, amount: number) => {
  const minimumFractionDigits = integerCurrencies.has(currency) ? 0 : 2;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits,
  }).format(amount);
};

const buildSummary = (session: SessionState, totals: Totals) => {
  const lines: string[] = [];
  lines.push(`BillSplit Summary`);
  lines.push(`Session: ${session.id}`);
  lines.push(`Generated: ${new Date().toLocaleString()}`);
  lines.push("");
  lines.push("Orders");
  lines.push("------");
  session.orders.forEach((order) => {
    const persons = order.persons.join(", ");
    const description = order.description || "Order";
    const total = computeOrderTotal(order);
    const amountDisplay = order.perPerson
      ? `${formatCurrency(session.currency, order.amount)} each`
      : formatCurrency(session.currency, total);
    const multiplier =
      order.perPerson && order.persons.length > 1
        ? ` × ${order.persons.length}`
        : "";
    lines.push(
      `${description}: ${formatCurrency(session.currency, total)} (${amountDisplay}${multiplier}) · Shared by ${persons}`
    );
  });
  lines.push("");
  lines.push("Totals");
  lines.push("------");
  lines.push(`Subtotal: ${formatCurrency(session.currency, totals.subtotal)}`);
  lines.push(`Tip: ${formatCurrency(session.currency, totals.tip)}`);
  lines.push(`Total: ${formatCurrency(session.currency, totals.total)}`);
  lines.push("");
  lines.push("Per person");
  lines.push("-----------");
  session.diners.forEach((diner) => {
    const share = totals.shares[diner];
    lines.push(diner);
    lines.push(
      `  Items: ${formatCurrency(session.currency, share?.items ?? 0)}`
    );
    lines.push(
      `  Tip: ${formatCurrency(session.currency, share?.tip ?? 0)}`
    );
    lines.push(
      `  Total: ${formatCurrency(session.currency, share?.total ?? 0)}`
    );
  });
  return lines.join("\n");
};

const SessionPage = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { state, loading, error, commit, clearError, lastSyncedAt } =
    useSession(sessionId);
  const { apiKey } = useApiKeyContext();
  const [newDiner, setNewDiner] = useState("");
  const [orderAmount, setOrderAmount] = useState("");
  const [orderDescription, setOrderDescription] = useState("");
  const [orderPersons, setOrderPersons] = useState<string[]>([]);
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "failed">(
    "idle"
  );
  const [orderPerPerson, setOrderPerPerson] = useState(true);
  const [inviteCopyStatus, setInviteCopyStatus] = useState<
    "idle" | "copied" | "failed"
  >("idle");
  const [tipDraft, setTipDraft] = useState("");
  const [totalDraft, setTotalDraft] = useState("");
  const [tipDraftError, setTipDraftError] = useState<string | null>(null);
  const [totalDraftError, setTotalDraftError] = useState<string | null>(null);
  const [tipApplying, setTipApplying] = useState(false);
  const [totalApplying, setTotalApplying] = useState(false);
  const [groupSelection, setGroupSelection] = useState<string[]>([]);

  useEffect(() => {
    if (!state) {
      setOrderPersons([]);
      return;
    }
    setOrderPersons((prev) =>
      prev.filter((name) => state.diners.includes(name))
    );
  }, [state]);

  useEffect(() => {
    if (!state) {
      setTipDraft("");
      setTotalDraft("");
      setTipDraftError(null);
      setTotalDraftError(null);
      return;
    }
    setTipDraft("");
    setTotalDraft("");
    setTipDraftError(null);
    setTotalDraftError(null);
  }, [state?.tipPercentage, state?.totalPaid, state?.id]);

  const totals = useMemo(() => computeTotals(state), [state]);
  const groupTotals = useMemo(() => {
    if (!state || groupSelection.length === 0) {
      return null;
    }
    return groupSelection.reduce(
      (acc, diner) => {
        const share = totals.shares[diner];
        if (!share) {
          return acc;
        }
        return {
          items: acc.items + share.items,
          tip: acc.tip + share.tip,
          total: acc.total + share.total,
        };
      },
      { items: 0, tip: 0, total: 0 }
    );
  }, [totals, groupSelection, state]);

  const lastSyncedLabel = useMemo(() => {
    if (!lastSyncedAt) {
      return "Syncing…";
    }
    const diff = Date.now() - lastSyncedAt.getTime();
    if (diff < 5000) {
      return "Updated just now";
    }
    if (diff < 60000) {
      const seconds = Math.round(diff / 1000);
      return `Updated ${seconds}s ago`;
    }
    const minutes = Math.round(diff / 60000);
    return `Updated ${minutes} min ago`;
  }, [lastSyncedAt]);

  const inviteLink = useMemo(() => {
    if (!state || !apiKey || typeof window === "undefined") {
      return "";
    }
    const origin = window.location.origin;
    return `${origin}/session/${state.id}?key=${apiKey}`;
  }, [state, apiKey]);

  useEffect(() => {
    if (copyStatus === "copied") {
      const timer = setTimeout(() => setCopyStatus("idle"), 2000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [copyStatus]);

  useEffect(() => {
    if (inviteCopyStatus === "copied") {
      const timer = setTimeout(() => setInviteCopyStatus("idle"), 2000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [inviteCopyStatus]);

  const handleAddDiner = () => {
    const trimmed = newDiner.trim();
    if (!trimmed) {
      return;
    }
    void commit("addDiner", { name: trimmed });
    setNewDiner("");
  };

  const handleRemoveDiner = (name: string) => {
    if (window.confirm(`Remove ${name} from this session?`)) {
      void commit("removeDiner", { name });
    }
  };

  const handleAddOrder = () => {
    const amount = Number.parseFloat(orderAmount);
    if (!Number.isFinite(amount) || amount <= 0 || orderPersons.length === 0) {
      return;
    }
    void commit("addOrder", {
      amount,
      description: orderDescription.trim() || undefined,
      persons: orderPersons,
      perPerson: orderPerPerson,
    });
    setOrderAmount("");
    setOrderDescription("");
    setOrderPersons([]);
    setOrderPerPerson(true);
  };

  const handleRemoveOrder = (orderId: number) => {
    if (window.confirm("Remove this order?")) {
      void commit("removeOrder", { orderId });
    }
  };

  const handleClearAll = () => {
    if (
      window.confirm(
        "Clear all diners, orders, and reset tip? This cannot be undone."
      )
    ) {
      void commit("clearAll", {});
    }
  };

  const handleCopySummary = useCallback(async () => {
    if (!state) {
      return;
    }
    try {
      await navigator.clipboard.writeText(buildSummary(state, totals));
      setCopyStatus("copied");
    } catch {
      setCopyStatus("failed");
    }
  }, [state, totals]);

  const handleCopyInvite = useCallback(async () => {
    if (!inviteLink) {
      return;
    }
    try {
      await navigator.clipboard.writeText(inviteLink);
      setInviteCopyStatus("copied");
    } catch {
      setInviteCopyStatus("failed");
    }
  }, [inviteLink]);

  const togglePersonForOrder = (name: string) => {
    setOrderPersons((prev) =>
      prev.includes(name)
        ? prev.filter((value) => value !== name)
        : [...prev, name]
    );
  };

  const toggleGroupSelection = (name: string) => {
    setGroupSelection((prev) =>
      prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name]
    );
  };

  const clearGroupSelection = () => setGroupSelection([]);

  const selectAllForOrder = () => {
    if (!state) {
      return;
    }
    if (orderPersons.length === state.diners.length) {
      setOrderPersons([]);
    } else {
      setOrderPersons(state.diners);
    }
  };

  const handleTipModeChange = (mode: TipMode) => {
    void commit("updateTipMode", { tipMode: mode });
  };
  const handleTipDraftChange = (value: string) => {
    setTipDraft(value);
    setTipDraftError(null);
  };

  const handleTotalDraftChange = (value: string) => {
    setTotalDraft(value);
    setTotalDraftError(null);
  };

  const applyTipPercentage = useCallback(async () => {
    if (!state) {
      return;
    }
    setTipDraftError(null);
    const trimmed = tipDraft.trim();
    if (trimmed === "") {
      setTipDraftError("Enter a percentage");
      return;
    }
    const numeric = Number(trimmed);
    if (!Number.isFinite(numeric) || numeric < 0 || numeric > 1000) {
      setTipDraftError("Tip must be between 0 and 1000");
      return;
    }
    if (numeric === state.tipPercentage) {
      setTipDraft("");
      setTipDraftError(null);
      return;
    }
    setTipApplying(true);
    try {
      await commit("updateTipPercentage", { tipPercentage: numeric });
      setTipDraft("");
      setTipDraftError(null);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to update tip";
      setTipDraftError(message);
    } finally {
      setTipApplying(false);
    }
  }, [state, tipDraft, commit]);

  const applyTotalPaid = useCallback(async () => {
    if (!state) {
      return;
    }
    setTotalDraftError(null);
    const trimmed = totalDraft.trim();
    if (trimmed === "") {
      setTotalDraftError("Enter an amount");
      return;
    }
    const numeric = Number.parseFloat(trimmed);
    if (!Number.isFinite(numeric) || numeric < 0) {
      setTotalDraftError("Amount must be zero or greater");
      return;
    }
    if (numeric === state.totalPaid) {
      setTotalDraft("");
      setTotalDraftError(null);
      return;
    }
    setTotalApplying(true);
    try {
      await commit("updateTotalPaid", { totalPaid: numeric });
      setTotalDraft("");
      setTotalDraftError(null);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to update amount";
      setTotalDraftError(message);
    } finally {
      setTotalApplying(false);
    }
  }, [state, totalDraft, commit]);

  const handleCurrencyChange = (value: string) => {
    if (!state || state.currency === value) {
      return;
    }
    void commit("updateCurrency", { currency: value });
  };

  const orderStep = state && integerCurrencies.has(state.currency) ? "1" : "0.01";
  const activeOrderTotal =
    Number.parseFloat(orderAmount || "0") *
    (orderPerPerson ? Math.max(orderPersons.length, 1) : 1);

  if (loading) {
    return (
      <div className="session-loading">
        <div className="spinner" />
        <p>Loading session…</p>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="session-loading">
        <p>We could not find that session.</p>
        <button className="primary" onClick={() => navigate("/")}>
          Go back
        </button>
      </div>
    );
  }

  return (
    <div className="session-root">
      <header className="session-header">
        <div>
          <h1>Session {state.id.slice(0, 8)}</h1>
          <p>
            Created{" "}
            {new Date(state.createdAt).toLocaleString(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
            })}
          </p>
        </div>
        <div className="session-actions">
          <div className="session-status">
            <span className="dot online" />
            {lastSyncedLabel}
          </div>
          <button className="ghost" onClick={handleCopySummary}>
            {copyStatus === "copied"
              ? "Copied!"
              : copyStatus === "failed"
                ? "Copy failed"
                : "Copy summary"}
          </button>
          <button className="ghost danger" onClick={handleClearAll}>
            Clear all
          </button>
        </div>
        {inviteLink && (
          <div className="invite-link">
            <input
              type="text"
              value={inviteLink}
              readOnly
              onFocus={(event) => event.currentTarget.select()}
            />
            <button type="button" onClick={handleCopyInvite}>
              {inviteCopyStatus === "copied"
                ? "Invite copied!"
                : inviteCopyStatus === "failed"
                  ? "Copy failed"
                  : "Copy invite"}
            </button>
          </div>
        )}
      </header>

      {error && (
        <div className="session-error" role="alert">
          <span>{error}</span>
          <button type="button" onClick={clearError}>
            Dismiss
          </button>
        </div>
      )}

      <section className="session-grid">
        <div className="panel">
          <h2>Add order</h2>
          <div className="field">
            <label htmlFor="amount">Amount</label>
            <input
              id="amount"
              type="number"
              min="0"
              step={orderStep}
              value={orderAmount}
              onChange={(event) => setOrderAmount(event.target.value)}
              placeholder="0.00"
            />
            {orderPerPerson && orderPersons.length > 1 ? (
              <p className="hint">
                Total charged:{" "}
                {state
                  ? formatCurrency(state.currency, activeOrderTotal || 0)
                  : activeOrderTotal}
              </p>
            ) : null}
          </div>
          <div className="field">
            <label htmlFor="description">Description</label>
            <input
              id="description"
              type="text"
              value={orderDescription}
              placeholder="Optional details"
              onChange={(event) => setOrderDescription(event.target.value)}
            />
          </div>
          <div className="field">
            <div className="field-header">
              <label>Charge to</label>
              <button
                type="button"
                className="secondary small"
                onClick={selectAllForOrder}
              >
                {orderPersons.length === state.diners.length
                  ? "Clear"
                  : "Select all"}
              </button>
            </div>
            <p className="group-hint">
              Each person selected gets the item on their own bill.
            </p>
            <div className="chip-row">
              {state.diners.map((diner) => {
                const active = orderPersons.includes(diner);
                return (
                  <button
                    key={diner}
                    type="button"
                    className={active ? "chip active" : "chip"}
                    onClick={() => togglePersonForOrder(diner)}
                  >
                    {diner}
                  </button>
                );
              })}
            </div>
          </div>
          <label className="field checkbox">
            <input
              type="checkbox"
              checked={!orderPerPerson}
              onChange={(event) => setOrderPerPerson(!event.target.checked)}
            />
            <span>Enter total to split across selected diners</span>
          </label>
          <button
            className="primary wide"
            type="button"
            onClick={handleAddOrder}
            disabled={
              !orderAmount ||
              Number.parseFloat(orderAmount) <= 0 ||
              orderPersons.length === 0
            }
          >
            Add order
          </button>
        </div>

        <div className="panel">
          <h2>Orders</h2>
          {state.orders.length === 0 ? (
            <p className="muted">No orders yet.</p>
          ) : (
            <ul className="orders">
              {state.orders.map((order) => (
                <OrderItem
                  key={order.id}
                  order={order}
                  currency={state.currency}
                  onRemove={handleRemoveOrder}
                />
              ))}
            </ul>
          )}
        </div>

        <div className="panel">
          <h2>Summary</h2>
          <div className="summary">
            <SummaryRow
              label="Subtotal"
              value={formatCurrency(state.currency, totals.subtotal)}
            />
            <SummaryRow
              label="Tip"
              value={formatCurrency(state.currency, totals.tip)}
            />
            <SummaryRow
              label="Total"
              value={formatCurrency(state.currency, totals.total)}
            />
          </div>
          <div className="field">
            <label>Currency</label>
            <select
              value={state.currency}
              onChange={(event) => handleCurrencyChange(event.target.value)}
            >
              {currencyOptions.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
          </div>
          <TipControls
            tipMode={state.tipMode}
            currency={state.currency}
            currentTipPercentage={state.tipPercentage}
            currentTotalPaid={state.totalPaid}
            tipDraft={tipDraft}
            totalDraft={totalDraft}
            onModeChange={handleTipModeChange}
            onTipDraftChange={handleTipDraftChange}
            onTotalDraftChange={handleTotalDraftChange}
            onApplyTip={applyTipPercentage}
            onApplyTotal={applyTotalPaid}
            tipDraftError={tipDraftError}
            totalDraftError={totalDraftError}
            tipApplying={tipApplying}
            totalApplying={totalApplying}
            orderStep={orderStep}
          />
          <div className="shares">
            <h3>Per person</h3>
            {state.diners.map((diner) => {
              const share = totals.shares[diner];
              return (
                <div key={diner} className="share-card">
                  <strong>{diner}</strong>
                  <SummaryRow
                    label="Items"
                    value={formatCurrency(
                      state.currency,
                      share?.items ?? 0
                    )}
                  />
                  <SummaryRow
                    label="Tip"
                    value={formatCurrency(state.currency, share?.tip ?? 0)}
                  />
                  <SummaryRow
                    label="Total"
                    value={formatCurrency(state.currency, share?.total ?? 0)}
                  />
                </div>
              );
            })}
          </div>
          <div className="group-panel">
            <div className="group-header">
              <h3>Combined diners</h3>
              <button
                type="button"
                className="secondary small"
                onClick={clearGroupSelection}
                disabled={groupSelection.length === 0}
              >
                Clear
              </button>
            </div>
            <p className="group-hint">
              Select diners to view their combined total.
            </p>
            <div className="chip-row">
              {state.diners.map((diner) => {
                const active = groupSelection.includes(diner);
                return (
                  <button
                    key={`group-${diner}`}
                    type="button"
                    className={active ? "chip active" : "chip"}
                    onClick={() => toggleGroupSelection(diner)}
                  >
                    {diner}
                  </button>
                );
              })}
            </div>
            {groupSelection.length > 0 && groupTotals && (
              <div className="group-summary">
                <p className="group-summary-title">
                  {groupSelection.join(" + ")}
                </p>
                <SummaryRow
                  label="Items"
                  value={formatCurrency(state.currency, groupTotals.items)}
                />
                <SummaryRow
                  label="Tip"
                  value={formatCurrency(state.currency, groupTotals.tip)}
                />
                <SummaryRow
                  label="Total"
                  value={formatCurrency(state.currency, groupTotals.total)}
                />
              </div>
            )}
          </div>
        </div>

        <div className="panel">
          <h2>Diners</h2>
          <div className="field">
            <label htmlFor="new-diner">Add diner</label>
            <div className="input-row">
              <input
                id="new-diner"
                type="text"
                value={newDiner}
                onChange={(event) => setNewDiner(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleAddDiner();
                  }
                }}
              />
              <button
                type="button"
                className="primary"
                onClick={handleAddDiner}
                disabled={!newDiner.trim()}
              >
                Add
              </button>
            </div>
          </div>
          <div className="chip-grid">
            {state.diners.map((diner) => (
              <div key={diner} className="chip-card">
                <span>{diner}</span>
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => handleRemoveDiner(diner)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <div className="summary-row">
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const OrderItem = ({
  order,
  currency,
  onRemove,
}: {
  order: Order;
  currency: string;
  onRemove: (orderId: number) => void;
}) => {
  const total = computeOrderTotal(order);
  const perPersonNote = order.perPerson
    ? `${formatCurrency(currency, order.amount)} each`
    : formatCurrency(currency, total);
  return (
    <li className="order">
      <div>
        <p className="order-title">{order.description ?? "Order"}</p>
        <p className="order-meta">
          {perPersonNote}
          {order.perPerson && order.persons.length > 1
            ? ` · total ${formatCurrency(currency, total)}`
            : null}
          {" · "}
          {order.persons.join(", ")}
        </p>
      </div>
      <button
        type="button"
        className="icon-button"
        onClick={() => onRemove(order.id)}
        aria-label="Remove order"
      >
        ×
      </button>
    </li>
  );
};

const TipControls = ({
  tipMode,
  currency,
  currentTipPercentage,
  currentTotalPaid,
  tipDraft,
  totalDraft,
  onModeChange,
  onTipDraftChange,
  onTotalDraftChange,
  onApplyTip,
  onApplyTotal,
  tipDraftError,
  totalDraftError,
  tipApplying,
  totalApplying,
  orderStep,
}: {
  tipMode: TipMode;
  currency: string;
  currentTipPercentage: number;
  currentTotalPaid: number;
  tipDraft: string;
  totalDraft: string;
  onModeChange: (mode: TipMode) => void;
  onTipDraftChange: (value: string) => void;
  onTotalDraftChange: (value: string) => void;
  onApplyTip: () => void;
  onApplyTotal: () => void;
  tipDraftError: string | null;
  totalDraftError: string | null;
  tipApplying: boolean;
  totalApplying: boolean;
  orderStep: string;
}) => (
  <Fragment>
    <div className="field">
      <label htmlFor="tip-mode">Tip mode</label>
      <select
        id="tip-mode"
        value={tipMode}
        onChange={(event) => onModeChange(event.target.value as TipMode)}
      >
        <option value="percentage">Percentage</option>
        <option value="total">Total paid</option>
      </select>
    </div>
    {tipMode === "percentage" ? (
      <div className="field">
        <label htmlFor="tip-percentage">
          Tip percentage
          <span className="current-value">
            Current: {currentTipPercentage}%
          </span>
        </label>
        <input
          id="tip-percentage"
          type="number"
          min="0"
          max="1000"
          step="0.5"
          value={tipDraft}
          placeholder={String(currentTipPercentage)}
          onChange={(event) => onTipDraftChange(event.target.value)}
        />
        {tipDraftError && <p className="field-error">{tipDraftError}</p>}
        <button
          type="button"
          className="secondary"
          onClick={onApplyTip}
          disabled={tipApplying || tipDraft.trim() === ""}
        >
          {tipApplying ? "Applying…" : "Apply"}
        </button>
      </div>
    ) : (
      <div className="field">
        <label htmlFor="total-paid">
          Total amount paid
          <span className="current-value">
            Current: {formatCurrency(currency, currentTotalPaid)}
          </span>
        </label>
        <input
          id="total-paid"
          type="number"
          min="0"
          step={orderStep}
          value={totalDraft}
          placeholder={currentTotalPaid ? String(currentTotalPaid) : "0"}
          onChange={(event) => onTotalDraftChange(event.target.value)}
        />
        {totalDraftError && <p className="field-error">{totalDraftError}</p>}
        <button
          type="button"
          className="secondary"
          onClick={onApplyTotal}
          disabled={totalApplying || totalDraft.trim() === ""}
        >
          {totalApplying ? "Applying…" : "Apply"}
        </button>
      </div>
    )}
  </Fragment>
);

export default SessionPage;
